/**
 * Calendar dates the way people here type them — day first: `31. 12. 2026`.
 *
 * Never a browser `<input type="date">` for editing: that control orders its
 * fields by the BROWSER's language, so an English Chrome shows `mm/dd/yyyy`,
 * and a swapped day and month on a deadline is a wrong deadline (Haris,
 * 2026-09-09). Dates travel as plain `'YYYY-MM-DD'` strings, never as a JS
 * `Date` (no timezone can shift a calendar day).
 *
 * The accepted forms and the messages match the CRM's `parseCrmDate`, which
 * adds its own open-ended »do preklica« value on top (#646).
 */

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** `31.12.2026`, `31. 12. 2026`, `31/12/2026`, `31.12.26`, a trailing dot allowed. */
const DAY_FIRST_RE = /^(\d{1,2})\s*[./]\s*(\d{1,2})\s*[./]\s*(\d{2,4})\.?$/;

function isRealDate(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const probe = new Date(Date.UTC(y, m - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
}

function pad2(n: number): string {
  return n < 10 ? `0${String(n)}` : String(n);
}

/**
 * Typed text → `{ value: 'YYYY-MM-DD' }`, `{ value: null }` for a blank box, or
 * `{ error }` with a Slovenian sentence. ISO is accepted too; a two-digit year
 * splits at 70 (`26` → 2026, `85` → 1985). Impossible dates are refused.
 */
export function parseDayFirstDate(raw: string): { value: string | null } | { error: string } {
  const text = raw.trim();
  if (!text) return { value: null };
  const iso = ISO_DATE_RE.exec(text);
  if (iso) {
    return isRealDate(Number(iso[1]), Number(iso[2]), Number(iso[3]))
      ? { value: text }
      : { error: `Datum '${text}' ne obstaja.` };
  }
  const df = DAY_FIRST_RE.exec(text);
  if (df) {
    const d = Number(df[1]);
    const m = Number(df[2]);
    const rawY = Number(df[3]);
    const y = rawY < 100 ? (rawY < 70 ? 2000 + rawY : 1900 + rawY) : rawY;
    return isRealDate(y, m, d)
      ? { value: `${String(y)}-${pad2(m)}-${pad2(d)}` }
      : { error: `Datum '${text}' ne obstaja.` };
  }
  return { error: `'${text}' ni datum. Uporabite 31. 12. 2026 ali 2026-12-31.` };
}

/** `'2026-12-31'` → `'31. 12. 2026'`; blank / null / not a date → `''`. */
export function formatDayFirst(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = ISO_DATE_RE.exec(iso.slice(0, 10));
  if (!m) return '';
  return `${String(Number(m[3]))}. ${String(Number(m[2]))}. ${m[1]}`;
}
